import { hookFormToVaultData, type VaultFormData } from '@onefootprint/core';
import type { VaultData } from '@onefootprint/request-types';
import { getOtherInputFormName, OTHER_INPUT_SUFFIX, otherLabelValue } from './constants';

type FormData = Record<string, unknown>;

const resolveOtherValue = (data: FormData, key: string, value: unknown) => {
  if (value !== otherLabelValue.value) return value;
  return data[getOtherInputFormName(key)] ?? value;
};

/**
 * Transforms VaultFormData to VaultData for submission
 * When dropdown value is "other", uses the __other_input field value instead
 *
 * Handling of special values:
 * - empty string: converted to null (user cleared a text field)
 * - null: kept in submission (explicit clearing signal to backend)
 * - undefined: filtered out (field was never visible or touched)
 */
export const formatFormData = (formData: VaultFormData): VaultData => {
  const data = formData as FormData;

  const result = Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => !key.endsWith(OTHER_INPUT_SUFFIX))
      .map(([key, value]) => {
        const resolvedValue = resolveOtherValue(data, key, value);
        // Convert empty string to null (user cleared the field)
        if (resolvedValue === '') return [key, null];
        // Everything else passes through as-is
        return [key, resolvedValue];
      })
      .filter(([, value]) => value !== undefined),
  ) as VaultFormData;

  return hookFormToVaultData(result);
};
